/**
 * MilestoneMapPanel — Pickup & Delivery location maps.
 *
 * Shows only pickup and delivery milestones, each with a small map button.
 * Clicking the button toggles an embedded Google Map for that specific location.
 * No directions/routing — each milestone is an independent point.
 * Part of the compliance evidence chain (SS 626:2024, Domain 6: Milestones).
 */

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MapPin, Clock, User, Truck, Camera,
  CheckCircle2, ExternalLink, Image as ImageIcon, X, Map,
} from 'lucide-react';
import { api } from '@/lib/api';

// ── Types ──────────────────────────────────────────────────────────────────────

interface MilestoneRecord {
  id: string;
  milestone_code: string;
  milestone_label: string;
  sequence_no: number;
  planned_at: string | null;
  actual_at: string | null;
  deviation_minutes: number | null;
  source: string;
  location_name: string | null;
  gps_latitude: number | null;
  gps_longitude: number | null;
  evidence_type: string | null;
  evidence_url: string | null;
  remarks: string | null;
}

interface MilestoneMapPanelProps {
  jobId: string;
  driverName?: string;
  driverPhone?: string;
  vehicleNo?: string;
  /** Job pickup address — shown as fallback map when no GPS milestones */
  pickupLocation?: string;
  /** Job delivery address — shown as fallback map when no GPS milestones */
  deliveryLocation?: string;
}

/** Milestone codes we care about for map display */
const PICKUP_CODES = new Set(['ARRIVED_PICKUP', 'GATE_IN', 'LOADING', 'LOADED']);
const DELIVERY_CODES = new Set(['ARRIVED_DELIVERY', 'UNLOADING', 'UNLOADED', 'POD_CAPTURED', 'POD_VERIFIED']);

function isPickupOrDelivery(code: string): 'pickup' | 'delivery' | null {
  if (PICKUP_CODES.has(code)) return 'pickup';
  if (DELIVERY_CODES.has(code)) return 'delivery';
  return null;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function MilestoneMapPanel({
  jobId,
  driverName,
  driverPhone,
  vehicleNo,
  pickupLocation,
  deliveryLocation,
}: MilestoneMapPanelProps) {
  const [milestones, setMilestones] = useState<MilestoneRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [openMapId, setOpenMapId] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  const fetchMilestones = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get<{
        items: MilestoneRecord[];
        total: number;
      }>(`/tms/tr128/milestones?jobId=${encodeURIComponent(jobId)}&limit=100`);
      setMilestones(resp.items ?? []);
    } catch {
      setMilestones(getDemoMilestones());
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { void fetchMilestones(); }, [fetchMilestones]);

  // Filter to pickup & delivery only, with GPS
  const filtered = milestones.filter((m) => {
    const kind = isPickupOrDelivery(m.milestone_code);
    return kind !== null && m.gps_latitude != null && m.gps_longitude != null;
  });

  // Deduplicate: keep best milestone per type (pickup / delivery)
  const pickupMs = filtered.find((m) => isPickupOrDelivery(m.milestone_code) === 'pickup');
  const deliveryMs = filtered.find((m) => isPickupOrDelivery(m.milestone_code) === 'delivery');
  // Also show POD if different from delivery
  const podMs = milestones.find(
    (m) => (m.milestone_code === 'POD_CAPTURED' || m.milestone_code === 'POD_VERIFIED')
      && m.gps_latitude != null && m.evidence_url != null
      && m.id !== deliveryMs?.id,
  );

  const displayList = [pickupMs, deliveryMs, podMs].filter(Boolean) as MilestoneRecord[];

  if (loading) {
    return (
      <div className="text-[10px] text-muted-foreground py-1 animate-pulse">
        Loading GPS data...
      </div>
    );
  }

  if (displayList.length === 0) {
    // No GPS milestones — show address-based Google Maps embed if locations available
    const locations = [
      { label: 'Pickup', loc: pickupLocation, color: 'bg-blue-500', kind: 'pickup' as const },
      { label: 'Delivery', loc: deliveryLocation, color: 'bg-orange-500', kind: 'delivery' as const },
    ].filter((s) => s.loc && s.loc.trim() !== '' && s.loc !== '—');

    if (locations.length === 0) return null;

    return (
      <div className="space-y-2 mt-2">
        {locations.map(({ label, loc, color }) => (
          <div key={label} className="rounded border overflow-hidden">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-muted/20">
              <span className={`shrink-0 h-4 w-4 rounded-full ${color} flex items-center justify-center`}>
                <MapPin className="h-2.5 w-2.5 text-white" />
              </span>
              <span className="text-[10px] font-semibold">{label}</span>
              <span className="text-[10px] text-muted-foreground truncate flex-1">{loc}</span>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc!)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 text-[10px] text-primary hover:underline flex items-center gap-0.5"
              >
                Open <ExternalLink className="h-2.5 w-2.5" />
              </a>
            </div>
            <iframe
              title={`${label} address map`}
              src={`https://www.google.com/maps?q=${encodeURIComponent(loc!)}&z=14&output=embed`}
              className="w-full h-[180px] border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              sandbox="allow-scripts allow-same-origin allow-popups"
            />
          </div>
        ))}
        {driverName && (
          <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 px-1">
            <User className="h-3 w-3" /> {driverName}
            {vehicleNo && <><Truck className="h-3 w-3 ml-1" /> {vehicleNo}</>}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-1.5 mt-2">
        {displayList.map((m) => {
          const kind = isPickupOrDelivery(m.milestone_code);
          const isOpen = openMapId === m.id;
          const isPod = m.milestone_code === 'POD_CAPTURED' || m.milestone_code === 'POD_VERIFIED';
          const label = isPod ? 'POD' : kind === 'pickup' ? 'Pickup' : 'Delivery';
          const color = kind === 'pickup' ? 'bg-blue-500' : isPod ? 'bg-green-500' : 'bg-orange-500';
          const bgColor = kind === 'pickup' ? 'bg-blue-50' : isPod ? 'bg-green-50' : 'bg-orange-50';

          return (
            <div key={m.id} className={`rounded border ${isOpen ? bgColor : ''}`}>
              {/* Compact row: label + location + time + map button */}
              <div className="flex items-center gap-2 px-2 py-1.5">
                <span className={`shrink-0 h-4 w-4 rounded-full ${color} flex items-center justify-center`}>
                  <MapPin className="h-2.5 w-2.5 text-white" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold">{label}</span>
                    {m.location_name && (
                      <span className="text-[10px] text-muted-foreground truncate">{m.location_name}</span>
                    )}
                  </div>
                  {m.actual_at && (
                    <div className="text-[9px] text-muted-foreground flex items-center gap-1">
                      <Clock className="h-2 w-2" />
                      {formatDateTime(m.actual_at)}
                      {m.deviation_minutes != null && (
                        <Badge className={`text-[7px] px-0.5 py-0 ${
                          Math.abs(m.deviation_minutes) <= 15 ? 'bg-green-100 text-green-700'
                            : m.deviation_minutes <= 30 ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {m.deviation_minutes > 0 ? '+' : ''}{m.deviation_minutes}m
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                {/* Evidence indicator */}
                {m.evidence_url && (
                  <button
                    className="shrink-0 h-5 w-5 rounded flex items-center justify-center text-blue-500 hover:bg-blue-100"
                    onClick={() => setPhotoPreview(m.evidence_url)}
                    title="View evidence"
                  >
                    <Camera className="h-3 w-3" />
                  </button>
                )}
                {/* Map toggle button */}
                <Button
                  variant={isOpen ? 'default' : 'outline'}
                  size="sm"
                  className="shrink-0 h-5 w-5 p-0"
                  onClick={() => setOpenMapId(isOpen ? null : m.id)}
                  title={isOpen ? 'Hide map' : 'Show map'}
                >
                  <Map className="h-3 w-3" />
                </Button>
              </div>

              {/* Expanded map + details */}
              {isOpen && (
                <div className="border-t">
                  <iframe
                    title={`${label} location`}
                    src={`https://www.google.com/maps?q=${m.gps_latitude},${m.gps_longitude}&z=16&output=embed`}
                    className="w-full h-[200px] border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                  <div className="px-2 py-1.5 space-y-1 text-[10px]">
                    {/* GPS + Google Maps link */}
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-red-500" />
                      <span className="font-mono">{m.gps_latitude!.toFixed(6)}, {m.gps_longitude!.toFixed(6)}</span>
                      <a
                        href={`https://www.google.com/maps?q=${m.gps_latitude},${m.gps_longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline flex items-center gap-0.5"
                      >
                        Open <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    </div>
                    {/* Driver & vehicle */}
                    {driverName && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <User className="h-2.5 w-2.5" /> {driverName}
                        {driverPhone && <span>· {driverPhone}</span>}
                      </div>
                    )}
                    {vehicleNo && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Truck className="h-2.5 w-2.5" /> {vehicleNo}
                      </div>
                    )}
                    {/* Timing */}
                    {m.planned_at && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Clock className="h-2.5 w-2.5" /> Planned: {formatDateTime(m.planned_at)}
                      </div>
                    )}
                    {m.actual_at && (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-2.5 w-2.5 text-green-500" /> Actual: {formatDateTime(m.actual_at)}
                      </div>
                    )}
                    {/* Remarks */}
                    {m.remarks && (
                      <div className="text-muted-foreground italic">"{m.remarks}"</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Photo preview overlay */}
      {photoPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPhotoPreview(null)}
        >
          <div className="relative max-w-2xl w-full bg-white rounded-lg overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
              <span className="text-sm font-medium flex items-center gap-1.5">
                <ImageIcon className="h-4 w-4" /> Evidence Photo
              </span>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setPhotoPreview(null)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="p-2">
              <img src={photoPreview} alt="Delivery evidence" className="w-full h-auto max-h-[70vh] object-contain rounded" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-SG', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** Demo milestones — pickup + delivery only */
function getDemoMilestones(): MilestoneRecord[] {
  return [
    {
      id: 'demo-pickup', milestone_code: 'ARRIVED_PICKUP', milestone_label: 'Arrived at Pickup',
      sequence_no: 1, planned_at: '2025-03-14T10:30:00Z', actual_at: '2025-03-14T10:25:00Z',
      deviation_minutes: -5, source: 'GPS', location_name: 'PSA Pasir Panjang Terminal',
      gps_latitude: 1.2763, gps_longitude: 103.7900, evidence_type: 'PHOTO',
      evidence_url: 'https://placehold.co/800x600/22c55e/white?text=Container+Pickup+Photo', remarks: null,
    },
    {
      id: 'demo-delivery', milestone_code: 'ARRIVED_DELIVERY', milestone_label: 'Arrived at Delivery',
      sequence_no: 2, planned_at: '2025-03-14T12:30:00Z', actual_at: '2025-03-14T12:20:00Z',
      deviation_minutes: -10, source: 'GEOFENCE', location_name: 'Tuas Warehouse #7',
      gps_latitude: 1.3216, gps_longitude: 103.6370, evidence_type: 'PHOTO',
      evidence_url: 'https://placehold.co/800x600/3b82f6/white?text=Delivery+Arrival+Photo', remarks: null,
    },
    {
      id: 'demo-pod', milestone_code: 'POD_CAPTURED', milestone_label: 'POD Captured',
      sequence_no: 3, planned_at: '2025-03-14T13:00:00Z', actual_at: '2025-03-14T12:45:00Z',
      deviation_minutes: -15, source: 'DRIVER_APP', location_name: 'Tuas Warehouse #7',
      gps_latitude: 1.3216, gps_longitude: 103.6370, evidence_type: 'SIGNATURE',
      evidence_url: 'https://placehold.co/800x600/f97316/white?text=POD+Signature',
      remarks: 'Signed by: John Tan, Warehouse Supervisor',
    },
  ];
}
